import FooterWave2Icon from "@/public/site-assets/svg/footer-wave-2.svg";
import clsx from "classnames";
import { motion } from "framer-motion";
import useTeachingJobs from "hooks/useTeachingJobs";
import { useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";
import Buttons from "ui/Buttons/component";
import styles from "./styles.module.scss";
import Arrow1 from "./svg/arrow-1.svg";

export const MultiColumn = ({
  className,
  heading,
  items,
  imageWrapper,
  showWave,
  waveDirection,
  waveColor,
  waveBackground,
  showTopWave,
  topWaveDirection,
  topWaveColor,
  topWaveBackground,
}) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const fallbackPosts = useTeachingJobs();
  const postsToRender = items && items.length > 0 ? items : fallbackPosts;

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // Adjust threshold as needed, make it smaller for more sensitive detection
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    observer.observe(sectionRef.current);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileDevice = window.innerWidth <= 991; // Adjust threshold for mobile devices
      // setIsVisible(!mobileDevice); // Hide motion on mobile devices
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {showTopWave && (
        <div
          className={clsx(
            "footer",
            topWaveDirection === "down" && "rotate",
            topWaveColor,
            topWaveBackground,
          )}
          style={{ marginBottom: "-1px", zIndex: 1, position: "relative" }}
        >
          <FooterWave2Icon />
        </div>
      )}
      <section ref={sectionRef} className={clsx(className, styles.root)}>
        <Container>
          {heading && (
            <div className="heading">
              <h3 className={`h3 position-relative z-index-1`}>{heading}</h3>

              <span className="arrow-1 d-none d-lg-inline-block">
                <Arrow1 />
              </span>
            </div>
          )}

          <div className="d-flex flex-wrap gap-4 gap-lg-5 mt-5 mb-5 mb-lg-0 pt-lg-5">
            {postsToRender.map((post, index) => (
              <motion.div
                key={index}
                className="card bg-transparent text-center p-0"
                initial={{ opacity: 0, y: 50 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="column px-4 py-5 py-lg-0 p-lg-0">
                  <div className="d-flex d-lg-block justify-content-center align-items-center gap-5">
                    {imageWrapper ? (
                      <div className={`${imageWrapper} position-relative`}>
                        <img
                          className="mb-lg-4 position-relative img"
                          src={post?.icon?.url || "/images/placeholders/icon-placeholder.png"}
                          alt={post.title}
                          height="auto"
                        />
                      </div>
                    ) : (
                      <img
                        className="mb-lg-4 position-relative img"
                        src={post?.icon?.url || "/images/placeholders/icon-placeholder.png"}
                        alt={post.title}
                        height="auto"
                      />
                    )}
                  </div>
                  <div>
                    <div className="post-title mb-2">
                      <a href={`${post.url}`} target="_self">
                        {post.title}
                      </a>
                    </div>
                    {post.short_description && (
                      <div className="post-short-description mb-lg-5">
                        <div dangerouslySetInnerHTML={{ __html: post?.short_description }}></div>
                      </div>
                    )}
                  </div>
                  <Buttons
                    buttons={post.button}
                    className="d-flex align-items-center text-center"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
        {showWave && (
          <div
            className={clsx(
              "footer",
              waveDirection === "down" && "rotate",
              waveColor,
              waveBackground,
            )}
            style={{ marginTop: "-1px" }}
          >
            <FooterWave2Icon />
          </div>
        )}
      </section>
    </>
  );
};

export default MultiColumn;
